import { Component, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subject, Observable, Subscription, combineLatest } from 'rxjs';
import {
  map,
  distinctUntilChanged,
  debounceTime,
  filter
} from 'rxjs/operators';
import {
  GridReadyEvent,
  GridSizeChangedEvent,
  GridOptions,
  ICellRendererParams,
  CellMouseOutEvent
} from 'ag-grid-community';
import {
  InfiniteScrollingService,
  ReusableStreamProperty,
  shallowCompare
} from '@ipreo/muni-shared';
import { GameDayRootReducerState } from '../../../state/root.state';
import { HoverRecentOrder } from '../../../state/charts/actions';
import { ViewOrder } from '../../mappers/gameDay-orders.transfomations';
import { NegativeCasesService } from '../../services/negative-cases.service';
import { ALL_ORDERS } from '../../gameday.constants';
import { RecentOrdersLeftCellRendererComponent } from './recent-orders-left-cell-renderer.component';
import { getRecentOrders } from './recent-orders.calculations';
import { getTrancheOrdersFilteredState } from '../../../state/data/reducer';
import { GameDayModel } from '../../../models/gameday-model';

@Component({
  selector: 'muni-gameday-recent-orders',
  templateUrl: './recent-orders.template.html',
  styleUrls: ['./recent-orders.styles.scss']
})
export class RecentOrdersComponent implements OnDestroy {
  public readonly hoveredViewOrder: Subject<
    ViewOrder | undefined
  > = new Subject<ViewOrder | undefined>();
  private readonly gridReadySubj = new Subject<GridReadyEvent>();

  @ReusableStreamProperty
  public readonly orders: Observable<GameDayModel.Order[]> = this.store.pipe(
    select(getTrancheOrdersFilteredState),
    map(({ orders }: { orders: GameDayModel.Order[] }) => orders),
    filter(orders => !!orders),
    distinctUntilChanged()
  );

  public readonly queryParams: Observable<string>;

  @ReusableStreamProperty
  public readonly recentOrders: Observable<ViewOrder[]> = this.orders.pipe(
    map(getRecentOrders),
    distinctUntilChanged(shallowCompare)
  );

  @ReusableStreamProperty
  public readonly length: Observable<number> = this.recentOrders.pipe(
    map(order => order.length),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly emptyMessage: Observable<
    string
  > = this.negativeCasesService.messages.pipe(
    map(messages => messages.orders),
    distinctUntilChanged()
  );

  public readonly hovered: Observable<GameDayModel.Order> = combineLatest(
    this.orders,
    this.hoveredViewOrder.asObservable().pipe(debounceTime(0))
  ).pipe(
    map(([orders, hoveredViewOrder]: [GameDayModel.Order[], ViewOrder]) =>
      hoveredViewOrder
        ? orders.find(
            order => order.orderNumber === hoveredViewOrder.orderNumber
          )
        : undefined
    ),
    distinctUntilChanged()
  );

  private readonly hoveredSubscription: Subscription = this.hovered.subscribe(
    (order: GameDayModel.Order) => (this.hoveredOrder = order)
  );

  public readonly allOrdersUrl: string = ALL_ORDERS;

  public readonly gridOptions: GridOptions = {
    columnDefs: [
      {
        headerName: 'Account',
        field: 'account',
        cellRenderer: 'recentOrdersLeftCellRenderer'
      },
      {
        headerName: 'Manager',
        field: 'manager',
        cellRenderer: ({ data }: ICellRendererParams) => {
          if (!data) {
            return null;
          }

          return `
                    <div class="recent-orders-right-cell-container">
                      <div class="amount">${data.amountFormatted}</div>
                      <div class="maturity">${data.maturity}</div>
                    </div>
                  `;
        }
      }
    ],
    headerHeight: 0,
    rowModelType: 'infinite',
    rowHeight: 40,
    frameworkComponents: {
      recentOrdersLeftCellRenderer: RecentOrdersLeftCellRendererComponent
    },
    onCellMouseOver: (event: CellMouseOutEvent) =>
      event.data ? this.hoveredViewOrder.next(event.data) : null,
    onCellMouseOut: () => this.hoveredViewOrder.next(undefined),
    onGridSizeChanged: (event: GridSizeChangedEvent) => {
      if (event) {
        event.api.sizeColumnsToFit();
      }
    },
    onGridReady: (event: GridReadyEvent) => {
      this.gridReadySubj.next(event);
    }
  };

  @ReusableStreamProperty
  private readonly grid$: Observable<
    [GridReadyEvent, ViewOrder[]]
  > = combineLatest(this.gridReadySubj, this.recentOrders);

  private readonly gridSubscription: Subscription = this.grid$.subscribe(
    ([{ api }, viewOrders]: [GridReadyEvent, ViewOrder[]]) => {
      const dataSource = this.infiniteScrollingService.getDataSource(
        viewOrders
      );
      api.setDatasource(dataSource);
    }
  );

  private set hoveredOrder(order: GameDayModel.Order) {
    if (!order) {
      this.store.dispatch(new HoverRecentOrder());
    } else {
      this.store.dispatch(new HoverRecentOrder(order));
    }
  }

  constructor(
    private store: Store<GameDayRootReducerState>,
    private negativeCasesService: NegativeCasesService,
    private infiniteScrollingService: InfiniteScrollingService
  ) {}

  public ngOnDestroy(): void {
    this.hoveredSubscription.unsubscribe();
    this.gridSubscription.unsubscribe();
  }
}
